import React from "react";
import { toast } from "sonner";
import { Paperclip, CircleNotch, FileText } from "@phosphor-icons/react";
import api, { API_BASE, formatApiError } from "@/lib/api";

const IMG_RE = /\.(jpe?g|png|gif|webp)(\?|$)/i;
const proxied = (url) => `${API_BASE}/inbox/attachment?url=${encodeURIComponent(url)}`;

// Renders Channex message attachments: images inline, other files as links.
export function MessageAttachments({ attachments, light }) {
  if (!attachments?.length) return null;
  return (
    <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="inbox-msg-attachments">
      {attachments.map((a, i) =>
        IMG_RE.test(a) ? (
          <a key={i} href={proxied(a)} target="_blank" rel="noreferrer">
            <img src={proxied(a)} alt={`Privitak ${i + 1}`} loading="lazy" className="max-h-48 max-w-[220px] rounded-md object-cover"
              onError={(e) => { e.currentTarget.replaceWith(Object.assign(document.createElement("span"), { textContent: `📎 Privitak ${i + 1}`, className: "text-xs underline" })); }} />
          </a>
        ) : (
          <a key={i} href={proxied(a)} target="_blank" rel="noreferrer"
            className={`inline-flex items-center gap-1 rounded px-2 py-1 text-xs underline ${light ? "bg-primary-foreground/15" : "bg-muted"}`}>
            <FileText size={13} /> Privitak {i + 1}
          </a>
        )
      )}
    </div>
  );
}

// Paperclip button: uploads one image/PDF and sends it to the OTA thread.
export function AttachmentButton({ threadId, disabled, onSent }) {
  const inputRef = React.useRef(null);
  const [busy, setBusy] = React.useState(false);
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("Datoteka je veća od 5 MB."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/inbox/threads/${threadId}/attachment`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Privitak poslan");
      onSent?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp,application/pdf" className="hidden" onChange={onFile} data-testid="inbox-attach-input" />
      <button type="button" onClick={() => inputRef.current?.click()} disabled={disabled || busy} title="Pošalji sliku ili PDF"
        className="flex items-center self-stretch rounded-md border border-foreground/20 bg-card px-2.5 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-40"
        data-testid="inbox-attach-btn">
        {busy ? <CircleNotch size={15} className="animate-spin" /> : <Paperclip size={15} />}
      </button>
    </>
  );
}
